import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import Typography from 'kingpin/atoms/Typography';
import Alert from 'kingpin/Alert';
import Loading from 'components/Loading/Loading';
import useShouldAggregateSignOff from 'components/PerformanceBoards/hooks/useShouldAggregateSignOff';
import useColumnOrder from '../hooks/useColumnOrder';
import PerformanceBoardSettingsContext from '../../contexts/PerformanceBoardSettingsContext';
import BoardSettingsHeader from '../BoardSettingsHeader/BoardSettingsHeader';
import FieldsGrid from './FieldsGrid/FieldsGrid';
import TextInput from '../../../../../kingpin/atoms/TextInput';
import useGetFieldDisplayName from './getFieldDisplayName';

const BoardFieldsSettings = ({
  columnOrder,
  updateColumnOrder,
}: {
  columnOrder: PerformanceBoardTypes.Column[];
  updateColumnOrder: ({
    newOrder,
  }: {
    newOrder: PerformanceBoardTypes.Column[];
  }) => void;
}) => {
  const [searchText, setSearchText] = useState<string>('');
  const [isFieldsFound, setIsFieldsFound] = useState<boolean>(true);
  const getFieldDisplayName = useGetFieldDisplayName();
  const { board } = useContext(PerformanceBoardSettingsContext);
  const shouldAggregate = useShouldAggregateSignOff(board);

  const filteredColumns = columnOrder.filter((c) =>
    getFieldDisplayName(c).toLowerCase().includes(searchText.toLowerCase()),
  );

  useEffect(() => {
    setIsFieldsFound(filteredColumns.length !== 0);
  }, [filteredColumns.length, setIsFieldsFound]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: shouldAggregate ? undefined : '141px' }}>
        <div style={{ paddingRight: '48px' }}>
          <BoardSettingsHeader
            title="Field Order"
            description="Configure your table order or fields"
          />
        </div>
        <div style={{ marginBottom: '16px', paddingRight: '48px' }}>
          <TextInput
            value={searchText}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setSearchText(event.target.value);
            }}
            placeholder="Search Fields"
            icon="search"
            width="100%"
            autoFocus
          />
        </div>
      </div>
      <div style={{ marginRight: '45px', flex: 1, overflowY: 'scroll' }}>
        {shouldAggregate && (
          <div style={{ marginBottom: '16px' }}>
            <Alert
              type="Warning"
              title="Values are summed across the sign-off period"
              body="This board totals each column across every week in the sign-off period. Numeric fields are added together, which is not accurate for averages or ratios (e.g. MPG) or for counts (e.g. FMCSA violations). Hide or ignore those columns here — only additive fields (e.g. miles, pay) total correctly."
            />
          </div>
        )}
        {isFieldsFound ? (
          <FieldsGrid
            searchText={searchText}
            filteredColumns={filteredColumns}
            columnOrder={columnOrder}
            updateColumnsOrder={updateColumnOrder}
          />
        ) : (
          <Typography.Body type="Body 12">There is no fields</Typography.Body>
        )}
      </div>
    </div>
  );
};

const Gate = () => {
  const { isLoading, columnOrder, updateColumnOrder } = useColumnOrder();

  if (isLoading) {
    return (
      <div style={{ position: 'absolute', left: 'calc(50% + 48px)' }}>
        <Loading />
      </div>
    );
  }
  if (!columnOrder) {
    return null;
  }
  return (
    <BoardFieldsSettings
      columnOrder={columnOrder}
      updateColumnOrder={updateColumnOrder}
    />
  );
};

export default Gate;
